import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MonitorSmartphone, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Footer, cardCls } from "@/components/mobile/parts";
import { PushCard } from "@/components/mobile/PushCard";
import { useAuth } from "@/context/AuthContext";
import { useMobile } from "@/context/MobileContext";
import { api, errMsg, ROLE_LABELS, isManagerRole } from "@/lib/api";
import { APP_VERSION, isDirect } from "@/lib/mobile";
import { cn } from "@/lib/utils";

const Section = ({ children }) => <p className="px-1 pt-2 text-sm font-bold text-slate-700">{children}</p>;
const GUIDE = "No sistema completo: Sistema → Usuários → editar o usuário → aba Configurações.";

function DirectSale({ session, refresh }) {
  const u = session.user;
  const manager = isManagerRole(u);
  const canSelf = !manager && !!u.direct_sale_self;
  const editable = manager || canSelf;
  const saved = manager ? !!session.tenant.settings?.mobile_direct_sale : isDirect(u, session.tenant);
  const [on, setOn] = useState(saved);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(manager ? "/mobile/settings" : "/auth/me/direct-sale", { direct_sale: on });
      await refresh();
      toast.success(`Venda direta ${on ? "ativada" : "desativada"} ${manager ? "na loja" : "para você"}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <Section>Funcionamento</Section>
      <label className={cn(cardCls, "flex items-start gap-3 p-4", !editable && "opacity-80")} data-testid="m-config-direct-sale-row">
        <Checkbox checked={on} onCheckedChange={(v) => setOn(!!v)} disabled={!editable || busy} className="mt-0.5 h-6 w-6 rounded-md border-2 border-[#1565c0] data-[state=checked]:bg-[#1565c0]" data-testid="m-config-direct-sale" />
        <span className="flex-1 space-y-1">
          <span className="block text-base font-semibold">{manager ? "Venda Direta (loja toda)" : "Venda Direta"}</span>
          <span className="block text-xs text-slate-500">{manager ? "Todos os celulares da loja só fazem venda direta (ticket), menos os usuários com Venda Direta própria no cadastro." : "Neste usuário o app só faz venda direta (ticket). Abrir nova comanda fica bloqueado."}</span>
          {canSelf && <span className="block text-xs font-semibold text-emerald-700" data-testid="m-config-direct-self">Você pode mudar. Vale em qualquer celular em que você entrar.</span>}
          {!editable && <span className="block text-xs font-semibold text-amber-700" data-testid="m-config-direct-locked">Só gerente ou proprietário pode mudar. {GUIDE}</span>}
          {manager && u.direct_sale != null && <span className="block text-xs font-semibold text-[#1565c0]" data-testid="m-config-direct-own">Seu usuário tem Venda Direta própria: {u.direct_sale ? "ligada" : "desligada"}.</span>}
          {manager && <span className="block text-xs text-slate-500">Para ligar ou desligar só para um usuário (e deixar ele mudar no app): {GUIDE}</span>}
        </span>
      </label>
      {editable && (
        <Footer className="border-t bg-white p-4" testId="m-config-save-bar">
          <Button onClick={save} disabled={busy || on === saved} className="h-14 w-full rounded-xl bg-emerald-600 text-lg font-bold text-white hover:bg-emerald-700" data-testid="m-config-save-btn">
            {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}Salvar
          </Button>
        </Footer>
      )}
    </>
  );
}

export function EmpresaTab() {
  const { session, refresh } = useAuth();
  const { queue } = useMobile();
  const nav = useNavigate();
  const t = session.tenant;
  const clearCache = () => {
    Object.keys(localStorage).filter((k) => k.startsWith("m_cache_")).forEach((k) => localStorage.removeItem(k));
    toast.success("Dados guardados para uso sem internet foram apagados");
  };
  return (
    <div className="space-y-4" data-testid="m-config-empresa">
      <div className={cn(cardCls, "space-y-0.5 p-4")} data-testid="m-config-account">
        <p className="text-xs font-semibold text-slate-500">Empresa ativa</p>
        <p className="font-display text-lg font-bold uppercase leading-tight" data-testid="m-config-company">{t.razao_social || t.nome_fantasia}</p>
        <p className="text-sm text-slate-500">{session.user.name} · {ROLE_LABELS[session.user.role]} · login {session.user.login}</p>
      </div>
      <DirectSale session={session} refresh={refresh} />
      <Section>Notificações</Section>
      <PushCard manager={isManagerRole(session.user)} />
      <Section>Aparelho</Section>
      <Button variant="outline" className="h-12 w-full justify-start" onClick={() => nav("/m/primeiro-acesso")} data-testid="m-config-welcome-btn"><Download className="mr-2 h-5 w-5" />Instalar o app e ativar avisos</Button>
      <Button variant="outline" className="h-12 w-full justify-start" onClick={() => nav("/app")} data-testid="m-config-full-btn"><MonitorSmartphone className="mr-2 h-5 w-5" />Abrir o sistema completo</Button>
      <Button variant="outline" className="h-12 w-full justify-start" onClick={clearCache} disabled={queue.length > 0} data-testid="m-config-clear-cache-btn"><Trash2 className="mr-2 h-5 w-5" />Apagar dados guardados no celular</Button>
      {queue.length > 0 && <p className="text-xs text-amber-700">Há envios guardados no celular. Envie-os antes de apagar os dados.</p>}
      <p className="pt-4 text-center text-xs text-slate-400">Versão {APP_VERSION}</p>
    </div>
  );
}
